/**
 * Load testing utilities for validating system performance
 * Simulates concurrent users and measures system response
 */

import { analytics } from '../services/analytics'

interface LoadTestConfig {
  concurrentUsers: number
  testDurationMs: number
  actionIntervalMs: number
  enableAnalytics: boolean
  targetEndpoint?: string
}

interface LoadTestResult {
  totalRequests: number
  successfulRequests: number
  failedRequests: number
  averageResponseTime: number
  maxResponseTime: number
  minResponseTime: number
  requestsPerSecond: number
  errorRate: number
  errors: Array<{ timestamp: number; error: string }>
}

interface UserSession {
  sessionId: string
  startTime: number
  actions: Array<{ action: string; timestamp: number; responseTime?: number; success: boolean }>
  currentState: 'landed' | 'playing' | 'paused' | 'survey' | 'completed' | 'error'
}

class LoadTester {
  private config: LoadTestConfig
  private sessions: Map<string, UserSession> = new Map()
  private testStartTime: number = 0
  private testEndTime: number = 0
  private results: LoadTestResult = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    averageResponseTime: 0,
    maxResponseTime: 0,
    minResponseTime: Infinity,
    requestsPerSecond: 0,
    errorRate: 0,
    errors: []
  }

  constructor(config: LoadTestConfig) {
    this.config = config
  }

  // Simulate a single user session
  private async simulateUserSession(userId: string): Promise<void> {
    const sessionId = `load-test-${userId}-${Date.now()}`
    const session: UserSession = {
      sessionId,
      startTime: Date.now(),
      actions: [],
      currentState: 'landed'
    }
    
    this.sessions.set(sessionId, session)

    try {
      // User lands on page
      await this.simulateAction(session, 'page_load', async () => {
        if (this.config.enableAnalytics) {
          await analytics.trackSessionStart()
        }
        await this.simulateNetworkDelay(100, 500) // Simulate page load
      })
      session.currentState = 'landed'

      // Wait before starting audio (user reading/preparing)
      await this.simulateDelay(1000, 3000)

      // Start audio playback
      await this.simulateAction(session, 'audio_start', async () => {
        if (this.config.enableAnalytics) {
          await analytics.trackAudioPlayStart(Math.random() * 3000 + 1000) // Random TTFP
        }
        await this.simulateNetworkDelay(500, 2000) // Simulate audio loading
      })
      session.currentState = 'playing'

      // Simulate listening period with random interactions
      const listeningDuration = Math.random() * 300000 + 60000 // 1-6 minutes
      const interactions = Math.floor(Math.random() * 5) + 1 // 1-5 interactions

      for (let i = 0; i < interactions; i++) {
        await this.simulateDelay(listeningDuration / interactions * 0.8, listeningDuration / interactions * 1.2)
        
        const action = Math.random() < 0.3 ? 'pause' : 'resume'
        await this.simulateAction(session, action, async () => {
          if (this.config.enableAnalytics) {
            const currentTime = Math.random() * 1800 // Random position
            const duration = Math.random() * 1800 + currentTime
            await analytics.trackAudioPlayPause(currentTime, duration)
          }
          await this.simulateNetworkDelay(50, 200)
        })
        
        session.currentState = action === 'pause' ? 'paused' : 'playing'
      }

      // Simulate 5-minute milestone (if listening long enough)
      if (listeningDuration > 300000) {
        await this.simulateAction(session, '5min_milestone', async () => {
          if (this.config.enableAnalytics) {
            await analytics.trackAudio5MinMilestone(300)
            await analytics.trackSurveyDisplayed()
          }
          await this.simulateNetworkDelay(100, 300)
        })
        session.currentState = 'survey'

        // Simulate survey response (70% completion rate)
        if (Math.random() < 0.7) {
          await this.simulateDelay(2000, 10000) // User thinks
          const ratings = ['excellent', 'good', 'fair', 'poor'] as const
          const rating = ratings[Math.floor(Math.random() * ratings.length)]
          
          await this.simulateAction(session, 'survey_complete', async () => {
            if (this.config.enableAnalytics) {
              await analytics.trackSurveyCompleted(rating)
            }
            await this.simulateNetworkDelay(200, 500)
          })
        } else {
          await this.simulateAction(session, 'survey_skip', async () => {
            if (this.config.enableAnalytics) {
              await analytics.trackSurveySkipped()
            }
            await this.simulateNetworkDelay(100, 200)
          })
        }
      }

      // End session (80% completion rate)
      if (Math.random() < 0.8) {
        await this.simulateAction(session, 'completion', async () => {
          if (this.config.enableAnalytics) {
            await analytics.trackCompletionReached(Math.random() * 1800 + 600)
          }
          await this.simulateNetworkDelay(200, 500)
        })
        session.currentState = 'completed'
      }

      // Track session end
      await this.simulateAction(session, 'session_end', async () => {
        if (this.config.enableAnalytics) {
          await analytics.trackSessionEnd()
        }
        await this.simulateNetworkDelay(100, 300)
      })

    } catch (error) {
      // Simulate error handling
      session.currentState = 'error'
      await this.simulateAction(session, 'error', async () => {
        if (this.config.enableAnalytics) {
          await analytics.trackError('general', error instanceof Error ? error.message : 'Load test error')
        }
        await this.simulateNetworkDelay(100, 500)
      })
      
      this.results.errors.push({
        timestamp: Date.now(),
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  private async simulateAction(session: UserSession, actionName: string, actionFn: () => Promise<void>): Promise<void> {
    const startTime = Date.now()
    let success = false
    
    try {
      await actionFn()
      success = true
      this.results.successfulRequests++
    } catch (error) {
      success = false
      this.results.failedRequests++
      this.results.errors.push({
        timestamp: Date.now(),
        error: error instanceof Error ? error.message : 'Action failed'
      })
    }
    
    const responseTime = Date.now() - startTime
    this.results.totalRequests++
    
    // Update response time statistics
    if (responseTime > this.results.maxResponseTime) {
      this.results.maxResponseTime = responseTime
    }
    if (responseTime < this.results.minResponseTime) {
      this.results.minResponseTime = responseTime
    }
    
    session.actions.push({
      action: actionName,
      timestamp: startTime,
      responseTime,
      success
    })
  }

  private async simulateNetworkDelay(minMs: number, maxMs: number): Promise<void> {
    const delay = Math.random() * (maxMs - minMs) + minMs
    return new Promise(resolve => setTimeout(resolve, delay))
  }

  private async simulateDelay(minMs: number, maxMs: number): Promise<void> {
    const delay = Math.random() * (maxMs - minMs) + minMs
    return new Promise(resolve => setTimeout(resolve, delay))
  }

  // Run the load test
  public async runLoadTest(): Promise<LoadTestResult> {
    console.log(`Starting load test with ${this.config.concurrentUsers} concurrent users for ${this.config.testDurationMs}ms`)
    
    this.testStartTime = Date.now()
    const userPromises: Promise<void>[] = []

    // Start concurrent user sessions
    for (let i = 0; i < this.config.concurrentUsers; i++) {
      const userPromise = this.simulateUserSession(`user-${i}`)
      userPromises.push(userPromise)
      
      // Stagger user starts slightly to simulate realistic traffic
      if (i < this.config.concurrentUsers - 1) {
        await this.simulateDelay(50, 200)
      }
    }

    // Wait for test duration or all users to complete
    const testTimeout = new Promise<void>(resolve => {
      setTimeout(resolve, this.config.testDurationMs)
    })

    await Promise.race([
      Promise.all(userPromises),
      testTimeout
    ])

    this.testEndTime = Date.now()
    
    // Calculate final statistics
    const testDurationSeconds = (this.testEndTime - this.testStartTime) / 1000
    this.results.requestsPerSecond = this.results.totalRequests / testDurationSeconds
    this.results.errorRate = (this.results.failedRequests / this.results.totalRequests) * 100
    
    // Calculate average response time
    let totalResponseTime = 0
    let responseTimeCount = 0
    
    for (const session of this.sessions.values()) {
      for (const action of session.actions) {
        if (action.responseTime) {
          totalResponseTime += action.responseTime
          responseTimeCount++
        }
      }
    }
    
    this.results.averageResponseTime = responseTimeCount > 0 ? totalResponseTime / responseTimeCount : 0
    
    if (this.results.minResponseTime === Infinity) {
      this.results.minResponseTime = 0
    }

    console.log('Load test completed:', this.results)
    return this.results
  }

  // Get detailed session information
  public getSessionDetails(): UserSession[] {
    return Array.from(this.sessions.values())
  }

  // Generate load test report
  public generateReport(): string {
    const report = `
Load Test Report
================
Test Configuration:
- Concurrent Users: ${this.config.concurrentUsers}
- Test Duration: ${this.config.testDurationMs}ms
- Analytics Enabled: ${this.config.enableAnalytics}

Results:
- Total Requests: ${this.results.totalRequests}
- Successful Requests: ${this.results.successfulRequests}
- Failed Requests: ${this.results.failedRequests}
- Error Rate: ${this.results.errorRate.toFixed(2)}%
- Requests per Second: ${this.results.requestsPerSecond.toFixed(2)}
- Average Response Time: ${this.results.averageResponseTime.toFixed(0)}ms
- Min Response Time: ${this.results.minResponseTime.toFixed(0)}ms
- Max Response Time: ${this.results.maxResponseTime.toFixed(0)}ms

Session Summary:
- Total Sessions: ${this.sessions.size}
- Completed Sessions: ${Array.from(this.sessions.values()).filter(s => s.currentState === 'completed').length}
- Error Sessions: ${Array.from(this.sessions.values()).filter(s => s.currentState === 'error').length}

${this.results.errors.length > 0 ? `
Recent Errors:
${this.results.errors.slice(-5).map(e => `- ${new Date(e.timestamp).toISOString()}: ${e.error}`).join('\n')}
` : 'No errors recorded.'}
`
    return report
  }
}

// Export load testing utilities
export { LoadTester, type LoadTestConfig, type LoadTestResult, type UserSession }

// Predefined test configurations
export const loadTestConfigs = {
  // Light load test (development)
  light: {
    concurrentUsers: 10,
    testDurationMs: 30000, // 30 seconds
    actionIntervalMs: 1000,
    enableAnalytics: true
  },
  
  // Medium load test (staging)
  medium: {
    concurrentUsers: 50,
    testDurationMs: 120000, // 2 minutes
    actionIntervalMs: 500,
    enableAnalytics: true
  },
  
  // Heavy load test (production validation)
  heavy: {
    concurrentUsers: 100,
    testDurationMs: 300000, // 5 minutes
    actionIntervalMs: 250,
    enableAnalytics: true
  },
  
  // Stress test (beyond normal capacity)
  stress: {
    concurrentUsers: 200,
    testDurationMs: 600000, // 10 minutes
    actionIntervalMs: 100,
    enableAnalytics: false // Disable to focus on core functionality
  }
}

// Performance monitoring during load tests
export const monitorPerformance = (): { memory: any; timing: any; network: any } => {
  const performance = window.performance as any
  
  return {
    memory: performance.memory ? {
      used: Math.round(performance.memory.usedJSHeapSize / 1048576),
      total: Math.round(performance.memory.totalJSHeapSize / 1048576),
      limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576)
    } : null,
    
    timing: performance.timing ? {
      navigationStart: performance.timing.navigationStart,
      loadEventEnd: performance.timing.loadEventEnd,
      domContentLoaded: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
      pageLoad: performance.timing.loadEventEnd - performance.timing.navigationStart
    } : null,
    
    network: (navigator as any).connection ? {
      effectiveType: (navigator as any).connection.effectiveType,
      downlink: (navigator as any).connection.downlink,
      rtt: (navigator as any).connection.rtt
    } : null
  }
}